// 2026-04-24: Rich text editor (WYSIWYG) cho nội dung chương nội quy
//
// Dùng TipTap (đã có trong package.json) + Image extension + Link extension.
//
// Tính năng:
//   - Bold / Italic / Strike / Code inline
//   - Heading H2, H3
//   - Bullet list / Numbered list
//   - Blockquote / Code block / Horizontal rule
//   - Link (prompt URL)
//   - Image (upload file hoặc paste từ clipboard)
//   - Undo / Redo
//
// Output: HTML (lưu vào field body_md — tên giữ nguyên, nội dung là HTML).
// NV reader tự detect HTML vs markdown khi render.
//
// Deps cần install (user tự chạy):
//   npm install @tiptap/extension-image @tiptap/extension-link

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import BaseImage from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
// M64 (2026-04-25): Table extension để giữ bảng từ Word import + admin sửa
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';

// 2026-04-24: Extend Image — cho phép admin resize bằng attribute width (%)
// Lưu dưới dạng <img src="..." style="width: 50%">.
const ResizableImage = BaseImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) =>
          el.getAttribute('width') || el.style?.width || null,
        renderHTML: (attrs) => {
          if (!attrs.width) return {};
          return { style: `width: ${attrs.width}` };
        },
      },
    };
  },
});
import { useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { showPrompt } from '../lib/dialogs.js';
// M74 (2026-04-25): convert markdown → HTML trước khi đẩy vào TipTap.
// AI generate ra **bold**, ## heading. TipTap không parse md → render literal.
import { toHtmlForEditor } from '../lib/renderRichText.js';

function ToolbarBtn({ active, disabled, onClick, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`px-2 py-1 text-sm rounded border transition ${
        active
          ? 'bg-sky-600 text-white border-sky-600'
          : 'bg-white hover:bg-slate-100 border-slate-300 text-slate-700'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function Sep() { return <span className="w-px h-5 bg-slate-200 mx-0.5" />; }

export default function RichTextEditor({ value, onChange, placeholder = 'Viết nội dung chương ở đây...', stickyToolbar = false }) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      // M48 (2026-04-25): allowBase64: true để hiển thị ảnh data: URI từ import docx.
      // (DOMPurify/renderRichText đã allowlist data:image/*;base64, nên hiển thị end-user OK.)
      ResizableImage.configure({ inline: false, allowBase64: true }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
      // M64 (2026-04-25): Table support — giữ bảng khi import Word + admin sửa
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    // M74 (2026-04-25): convert md → HTML trước khi mount (AI output có thể là markdown)
    content: toHtmlForEditor(value),
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        // min-h để click target đủ to; focus:outline-none bỏ viền xanh mặc định
        class: 'max-w-none focus:outline-none px-3 py-2 leading-relaxed',
        style: 'min-height: 240px;',
      },
      // Handle paste image
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imgItem = items.find((it) => it.type.startsWith('image/'));
        if (imgItem) {
          event.preventDefault();
          const file = imgItem.getAsFile();
          if (file) uploadAndInsert(file);
          return true;
        }
        return false;
      },
    },
  });

  // M61 (2026-04-25): Fix bug "modal Sửa câu hỏi không hiện nội dung lần đầu mở".
  //
  // useEditor chỉ dùng `content: value` LÚC mount — nếu prop value change sau đó
  // (vd modal đóng/mở với câu khác), editor giữ content cũ.
  //
  // QuestionEditor có `if (!open) return null` — đóng modal KHÔNG unmount component
  // (chỉ unmount UI), nên useEditor giữ instance cũ với content cũ. Lần mở 2 mới
  // re-render thành công vì state đã sync xong.
  //
  // Fix: useEffect watch value prop → setContent({ emitUpdate: false }) để không
  // trigger onUpdate (tránh loop onChange ↔ setContent).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    // M74 (2026-04-25): convert md → HTML trước khi setContent
    const next = toHtmlForEditor(value);
    // Tránh setContent khi đã trùng — tránh reset cursor khi user đang gõ
    // TipTap getHTML có thể wrap '<p></p>' cho empty → so sánh stripped
    const sameNormalized = (a, b) =>
      String(a).replace(/\s+/g, '').replace(/<p><\/p>/gi, '') ===
      String(b).replace(/\s+/g, '').replace(/<p><\/p>/gi, '');
    if (sameNormalized(current, next)) return;
    editor.commands.setContent(next, false);  // false = không emit onUpdate
  }, [editor, value]);

  // 2026-04-24 SEC (Phase 1): validate size/MIME trước khi gửi lên BE.
  // Chặn DoS qua paste file 50MB + chặn non-image extension (xlsx/exe rename thành .jpg).
  const ALLOWED_IMAGE_MIME = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB đủ ảnh JPEG 4K chất lượng 90

  async function uploadAndInsert(file) {
    if (!editor) return;
    // Guard 1: MIME phải là image whitelist
    if (!ALLOWED_IMAGE_MIME.includes(file.type)) {
      toast.error(`File không phải ảnh hợp lệ (nhận: ${file.type || 'không rõ'}). Dùng PNG/JPG/GIF/WEBP.`);
      return;
    }
    // Guard 2: size limit
    if (file.size > MAX_IMAGE_BYTES) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      toast.error(`Ảnh quá lớn (${mb}MB). Tối đa 5MB — hãy resize trước khi upload.`);
      return;
    }
    const t = toast.loading('Đang upload ảnh...');
    try {
      const res = await api.rulesUploadImage(file);
      toast.dismiss(t);
      if (res?.url) {
        editor.chain().focus().setImage({ src: res.url }).run();
        toast.success('Đã chèn ảnh');
      } else {
        toast.error('Upload fail: thiếu URL');
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error('Upload ảnh lỗi: ' + (e?.data?.error || e?.message));
    }
  }

  function onPickFile(e) {
    const file = e.target.files?.[0];
    if (file) uploadAndInsert(file);
    e.target.value = '';  // reset để chọn cùng file lần nữa OK
  }

  const setLink = useCallback(async () => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || '';
    const url = await showPrompt({
      title: 'Chèn link',
      message: 'URL (để trống để gỡ link):',
      defaultValue: prev,
      placeholder: 'https://...',
    });
    if (url === null) return;  // cancel
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  if (!editor) return <div className="text-sm text-slate-500 p-3">Đang tải editor...</div>;

  return (
    <div className="border border-slate-300 rounded-lg bg-white">
      {/* M101 (2026-04-26): toolbar sticky opt-in qua prop stickyToolbar.
          Default off (giữ behavior cũ — admin rules-modules đã quen). Trang KB
          có content rất dài → bật sticky để toolbar luôn visible khi scroll. */}
      <div className={`flex flex-wrap items-center gap-1 p-2 border-b border-slate-200 bg-slate-50 rounded-t-lg ${stickyToolbar ? 'sticky top-0 z-10 shadow-sm' : ''}`}>
        <ToolbarBtn title="Đậm (Ctrl+B)" active={editor.isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()}>
          <strong>B</strong>
        </ToolbarBtn>
        <ToolbarBtn title="Nghiêng (Ctrl+I)" active={editor.isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()}>
          <em>I</em>
        </ToolbarBtn>
        <ToolbarBtn title="Gạch ngang" active={editor.isActive('strike')}
                    onClick={() => editor.chain().focus().toggleStrike().run()}>
          <span className="line-through">S</span>
        </ToolbarBtn>
        <ToolbarBtn title="Inline code" active={editor.isActive('code')}
                    onClick={() => editor.chain().focus().toggleCode().run()}>
          {'</>'}
        </ToolbarBtn>
        <Sep />
        <ToolbarBtn title="Tiêu đề lớn" active={editor.isActive('heading', { level: 2 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          H2
        </ToolbarBtn>
        <ToolbarBtn title="Tiêu đề vừa" active={editor.isActive('heading', { level: 3 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          H3
        </ToolbarBtn>
        <Sep />
        <ToolbarBtn title="Dấu đầu dòng" active={editor.isActive('bulletList')}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}>
          • List
        </ToolbarBtn>
        <ToolbarBtn title="Đánh số" active={editor.isActive('orderedList')}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          1. List
        </ToolbarBtn>
        <Sep />
        <ToolbarBtn title="Trích dẫn" active={editor.isActive('blockquote')}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          ❝
        </ToolbarBtn>
        <ToolbarBtn title="Code block" active={editor.isActive('codeBlock')}
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
          {'{ }'}
        </ToolbarBtn>
        <ToolbarBtn title="Đường kẻ ngang"
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          ―
        </ToolbarBtn>
        <Sep />
        <ToolbarBtn title="Chèn link" active={editor.isActive('link')} onClick={setLink}>
          🔗 Link
        </ToolbarBtn>
        <ToolbarBtn title="Chèn ảnh (upload)" onClick={() => fileInputRef.current?.click()}>
          🖼 Ảnh
        </ToolbarBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={onPickFile}
        />
        <Sep />
        <ToolbarBtn title="Hoàn tác (Ctrl+Z)" disabled={!editor.can().undo()}
                    onClick={() => editor.chain().focus().undo().run()}>
          ↶
        </ToolbarBtn>
        <ToolbarBtn title="Làm lại (Ctrl+Shift+Z)" disabled={!editor.can().redo()}
                    onClick={() => editor.chain().focus().redo().run()}>
          ↷
        </ToolbarBtn>

        {/* 2026-04-24: Resize ảnh — chỉ hiện khi đang select ảnh */}
        {editor.isActive('image') && (
          <>
            <Sep />
            <span className="text-[11px] text-slate-600 font-medium px-1">Kích thước:</span>
            {['25%', '50%', '75%', '100%'].map((w) => {
              const active = editor.getAttributes('image').width === w;
              return (
                <ToolbarBtn
                  key={w}
                  title={`Ảnh rộng ${w}`}
                  active={active}
                  onClick={() => editor.chain().focus().updateAttributes('image', { width: w }).run()}
                >
                  {w}
                </ToolbarBtn>
              );
            })}
            <ToolbarBtn
              title="Xoá thuộc tính width (về kích thước gốc)"
              onClick={() => editor.chain().focus().updateAttributes('image', { width: null }).run()}
            >
              ↺
            </ToolbarBtn>
          </>
        )}

        <span className="ml-auto text-[10px] text-slate-400 italic hidden md:inline">
          💡 Paste ảnh từ clipboard · Click ảnh để resize
        </span>
      </div>

      {/* Editor body — click vào vùng trắng sẽ focus editor */}
      <div
        className="cursor-text"
        onClick={() => editor?.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </div>

      {/* CSS nội dung TipTap — heading/list/img/blockquote */}
      <style>{`
        .ProseMirror { outline: none; }
        .ProseMirror p { margin: 0.4em 0; }
        .ProseMirror h2 { font-size: 1.4em; font-weight: 700; margin: 0.8em 0 0.4em; color: #0c4a6e; }
        .ProseMirror h3 { font-size: 1.15em; font-weight: 600; margin: 0.6em 0 0.3em; color: #334155; }
        .ProseMirror ul { list-style: disc; padding-left: 1.5em; margin: 0.4em 0; }
        .ProseMirror ol { list-style: decimal; padding-left: 1.5em; margin: 0.4em 0; }
        .ProseMirror li { margin: 0.2em 0; }
        .ProseMirror blockquote { border-left: 3px solid #0ea5e9; padding-left: 1em; color: #475569; font-style: italic; margin: 0.6em 0; }
        .ProseMirror code { background: #f1f5f9; padding: 0.1em 0.3em; border-radius: 0.2em; font-family: ui-monospace, monospace; font-size: 0.9em; }
        .ProseMirror pre { background: #0f172a; color: #e2e8f0; padding: 0.8em; border-radius: 0.4em; overflow-x: auto; margin: 0.6em 0; }
        .ProseMirror pre code { background: transparent; padding: 0; color: inherit; }
        .ProseMirror hr { border: none; border-top: 2px solid #e2e8f0; margin: 1em 0; }
        .ProseMirror a { color: #0284c7; text-decoration: underline; }
        .ProseMirror img { max-width: 100%; height: auto; border-radius: 0.4em; margin: 0.6em 0; display: block; }
        .ProseMirror img.ProseMirror-selectednode { outline: 3px solid #0ea5e9; }
        /* Placeholder — hiện khi editor rỗng */
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #94a3b8;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
